import { useRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import React, { createContext, useContext, useMemo } from 'react'

import attributionFields from './attributionFields'
import checkSocialDomains from './checkSocialDomains'
import { getSessionStorageItem, setSessionStorageItem } from './sessionStorage'

export type AttributionParams = {
  utm_campaign?: string
  utm_content?: string
  utm_medium?: string
  utm_source?: string
  utm_term?: string
}

const SEARCH_ENGINE_HOSTNAMES = ['google', 'duckduckgo', 'bing', 'yahoo', 'baidu', 'yandex', 'ya']

const attributionParamsFromUrlParams = (urlParams: ParsedUrlQuery) =>
  Object.entries(urlParams).reduce<AttributionParams>((result, [key, value]) => {
    if (typeof key === 'string' && typeof value === 'string' && key.startsWith('utm_')) {
      setSessionStorageItem(key, value)
      return { ...result, [key]: value }
    }
    return result
  }, {})

const attributionParamsFromSessionStorage = () => {
  const sessionStorageItems = Object.entries(attributionFields).reduce<AttributionParams>(
    (result, [key]) => {
      const sessionStorageItem = getSessionStorageItem(key)
      if (sessionStorageItem) {
        return { ...result, [key]: sessionStorageItem }
      }
      return result
    },
    {}
  )
  return sessionStorageItems
}

const attributionParamsFromDocumentReferrer = () => {
  const docReferrer =
    typeof document !== 'undefined' && document.referrer && new URL(document.referrer)

  if (docReferrer && docReferrer.hostname) {
    const match = SEARCH_ENGINE_HOSTNAMES.find(se => {
      const sld = docReferrer.hostname.split('.').reverse()[1]
      return sld && sld === se
    })

    if (match) {
      const utm_medium = 'Organic'
      const utm_source = docReferrer.hostname
      setSessionStorageItem('utm_medium', utm_medium)
      setSessionStorageItem('utm_source', utm_source)
      return { utm_medium, utm_source }
    }
  }

  return {}
}

const AttributionParamsContext = createContext<AttributionParams>({})

const AttributionParamsProvider = ({ children }: { children: React.ReactNode }) => {
  const { query: urlParams } = useRouter()

  const attributionParams = useMemo(() => {
    const urlAttributionParams = attributionParamsFromUrlParams(urlParams)
    if (Object.keys(urlAttributionParams).length > 0) return urlAttributionParams

    const sessionStorageAttributionParams = attributionParamsFromSessionStorage()
    if (Object.keys(sessionStorageAttributionParams).length > 0) {
      return sessionStorageAttributionParams
    }
    const documentReferrerParams = attributionParamsFromDocumentReferrer()
    if (Object.keys(documentReferrerParams).length > 0) {
      return documentReferrerParams
    }
    const fallbackSocialDomains = checkSocialDomains()
    if (Object.keys(fallbackSocialDomains).length > 0) {
      return fallbackSocialDomains
    }

    const utm_medium = 'Unknown'
    const utm_source = 'Unknown'
    const utm_campaign =
      (typeof document !== 'undefined' &&
        document.referrer &&
        new URL(document.referrer).hostname) ||
      ''

    setSessionStorageItem('utm_medium', utm_medium)
    setSessionStorageItem('utm_source', utm_source)
    setSessionStorageItem('utm_campaign', utm_campaign)

    return { utm_campaign, utm_medium, utm_source }
  }, [urlParams])

  return (
    <AttributionParamsContext.Provider value={attributionParams}>
      {children}
    </AttributionParamsContext.Provider>
  )
}

export const useAttributionParams = () => useContext(AttributionParamsContext)

export default AttributionParamsProvider
